// SAD-516 RFI-05: client + query/mutation hooks for the dynamic RFI outline,
// section ownership and RFI-owned follow-ups (RfiSectionService, zou).
import { Timestamp } from '@bufbuild/protobuf';
import { createClient } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { RfiSectionService } from '../../../../../gen/ts/mono/projectdata/v1/rfi_sections_connect';
import { authClient } from '../../lib/connect/clients';
import { connectTransport } from '../../lib/connect/transport';
export const rfiSectionClient = createClient(RfiSectionService, connectTransport);
const SECTIONS_KEY = 'rfi-sections';
const FOLLOW_UPS_KEY = 'rfi-follow-ups';
export const WORK_STATES = [
    'not_started',
    'in_progress',
    'complete'
];
export const REVIEW_STATES = [
    'not_requested',
    'pending',
    'approved',
    'changes_requested'
];
function failed(fallback) {
    return (err)=>toast.error(err instanceof Error ? err.message : fallback);
}
export function useRfiSections(rfiResponseProjectId) {
    return useQuery({
        queryKey: [
            SECTIONS_KEY,
            rfiResponseProjectId
        ],
        enabled: Boolean(rfiResponseProjectId),
        queryFn: async ()=>(await rfiSectionClient.listRfiSections({
                rfiResponseProjectId
            })).sections
    });
}
/** Tenant users the owner/reviewer/assignee selects offer. */ export function useRfiPeople() {
    return useQuery({
        queryKey: [
            'rfi-outline-people'
        ],
        staleTime: 60000,
        queryFn: async ()=>{
            const resp = await authClient.listUsersForSharing({
                query: ''
            });
            return resp.users.map((u)=>({
                    userId: u.id,
                    name: u.name,
                    email: u.email
                }));
        }
    });
}
export function useRfiSectionMutations(rfiResponseProjectId) {
    const queryClient = useQueryClient();
    const refresh = ()=>queryClient.invalidateQueries({
            queryKey: [
                SECTIONS_KEY,
                rfiResponseProjectId
            ]
        });
    const create = useMutation({
        mutationFn: (a)=>rfiSectionClient.createRfiSection({
                rfiResponseProjectId,
                parentSectionId: a.parentSectionId ?? '',
                position: -1,
                title: a.title
            }),
        onSuccess: refresh,
        onError: failed('Failed to add section')
    });
    const update = useMutation({
        mutationFn: (a)=>rfiSectionClient.updateRfiSection({
                rfiResponseProjectId,
                sectionId: a.sectionId,
                title: a.title,
                ownerUserId: a.ownerUserId,
                reviewerUserId: a.reviewerUserId,
                clearReviewer: a.clearReviewer ?? false,
                internalDue: a.internalDue ? Timestamp.fromDate(a.internalDue) : undefined,
                clearInternalDue: a.clearInternalDue ?? false,
                workState: a.workState,
                reviewState: a.reviewState
            }),
        onSuccess: refresh,
        onError: failed('Failed to update section')
    });
    const move = useMutation({
        mutationFn: (a)=>rfiSectionClient.moveRfiSection({
                rfiResponseProjectId,
                ...a
            }),
        onSuccess: refresh,
        onError: failed('Failed to move section')
    });
    // No onError toast: a refusal is a normal response (removed=false + warning).
    const remove = useMutation({
        mutationFn: (a)=>rfiSectionClient.removeRfiSection({
                rfiResponseProjectId,
                ...a
            }),
        onSuccess: (resp)=>{
            if (resp.removed) refresh();
        },
        onError: failed('Failed to remove section')
    });
    return {
        create,
        update,
        move,
        remove
    };
}
export function useRfiFollowUps(rfiResponseProjectId) {
    return useQuery({
        queryKey: [
            FOLLOW_UPS_KEY,
            rfiResponseProjectId
        ],
        enabled: Boolean(rfiResponseProjectId),
        queryFn: async ()=>(await rfiSectionClient.listRfiFollowUps({
                rfiResponseProjectId,
                sectionId: '',
                status: ''
            })).followUps
    });
}
export function useRfiFollowUpMutations(rfiResponseProjectId) {
    const queryClient = useQueryClient();
    const refresh = ()=>queryClient.invalidateQueries({
            queryKey: [
                FOLLOW_UPS_KEY,
                rfiResponseProjectId
            ]
        });
    const create = useMutation({
        mutationFn: (a)=>rfiSectionClient.createRfiFollowUp({
                rfiResponseProjectId,
                sectionId: a.sectionId ?? '',
                captureProjectId: '',
                title: a.title,
                assigneeUserId: a.assigneeUserId ?? ''
            }),
        onSuccess: refresh,
        onError: failed('Failed to add follow-up')
    });
    const setStatus = useMutation({
        mutationFn: (a)=>rfiSectionClient.updateRfiFollowUp({
                rfiResponseProjectId,
                followUpId: a.followUpId,
                status: a.status
            }),
        onSuccess: refresh,
        onError: failed('Failed to update follow-up')
    });
    return {
        create,
        setStatus
    };
}
